import React, { useEffect, useMemo, useState } from 'react'
import { Autocomplete, Box, Button, MenuItem, TextField } from '@mui/material'
import { useSnackbar } from 'notistack'
import IntelLayout from './components/IntelLayout'
import Badge from './components/Badge'
import IntelTable from './components/IntelTable'
import { intelColors } from './components/intelTheme'
import { intelApi } from './api'
import { downloadCSV } from '../../helpers/UtilityHelper'
import { useGetAllCourierPartnersCachedQuery } from '../../redux/commonApiCalls'

const COURIER_FILTER = {
  andfilter: [{ field: 'is_deleted', operator: 'eq', value: '0' }],
  offset: 0,
  limit: 5000,
  skipCount: true,
  includes: 'id,name,prozoId,cpAccountCode',
}

const SCOPE_FILTERS = [
  { value: '', label: 'All' },
  { value: 'BOTH', label: 'Both Prepaid + COD' },
  { value: 'COD', label: 'COD Only' },
]

export default function BlockActive() {
  const { enqueueSnackbar } = useSnackbar()
  const { data: couriers = [] } = useGetAllCourierPartnersCachedQuery(COURIER_FILTER)

  const [courierFilter, setCourierFilter] = useState(null) // option object {value: prozoId, label: accountCode}
  const [scope, setScope] = useState('')
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)
  const [data, setData] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)

  // Labelled by account code (value = prozoId, used as the filter param).
  const courierOptions = useMemo(
    () =>
      (couriers || [])
        .filter((c) => c?.prozoId)
        .map((c) => ({ value: c.prozoId, label: c.cpAccountCode || c.name || c.prozoId }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [couriers]
  )

  useEffect(() => {
    let cancel = false
    setLoading(true)
    setErr(null)
    intelApi
      .blockActive({ prozoId: courierFilter?.value || '', scope, page: 1, size: 500 })
      .then((res) => {
        if (cancel) return
        setData(res?.data?.responseObj || res?.data?.data || res?.data)
      })
      .catch((e) => !cancel && setErr(e?.response?.data?.exception || e.message))
      .finally(() => !cancel && setLoading(false))
    return () => {
      cancel = true
    }
  }, [courierFilter, scope, reloadKey])

  const rows = data?.rows || []

  const unblock = async (row) => {
    const ids = row.ids && row.ids.length ? row.ids : row.id ? [row.id] : []
    if (!ids.length) return
    if (ids.length > 1 && !window.confirm(`Unblock pincode ${row.pincode} on ${ids.length} couriers?`)) return
    try {
      await intelApi.blockUnblockBulk(ids)
      enqueueSnackbar(`Unblocked ${ids.length} block(s)`, { variant: 'success' })
      setReloadKey((k) => k + 1)
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.exception || e.message, { variant: 'error' })
    }
  }

  const fmtTs = (s) => (s ? String(s).replace('T', ' ').slice(0, 16) : '—')

  const columns = [
    { key: 'pincode', header: 'Pincode' },
    {
      key: 'courier',
      header: 'Courier Account',
      render: (r) =>
        r.courierCount > 1 ? (
          <Badge color='blue'>{r.courierCount} couriers</Badge>
        ) : r.courierAccountCode ? (
          <code style={{ background: '#f1f3f5', padding: '1px 5px', borderRadius: 3 }}>{r.courierAccountCode}</code>
        ) : (
          '—'
        ),
    },
    {
      key: 'merchantScope',
      header: 'Merchant Scope',
      render: (r) =>
        r.merchantScope === 'ALL_MERCHANTS' ? <Badge color='grey'>All Merchants</Badge> : r.merchantOid || '—',
    },
    { key: 'scope', header: 'Block Scope', render: (r) => (r.scope ? <Badge color='orange'>{r.scope}</Badge> : '—') },
    { key: 'reason', header: 'Reason', render: (r) => (r.reason || r.reasonLabel ? <Badge color='red'>{r.reasonLabel || r.reason}</Badge> : '—') },
    { key: 'notes', header: 'Notes', render: (r) => r.notes || '—' },
    { key: 'blockedOn', header: 'Blocked On', render: (r) => fmtTs(r.blockedOn) },
    { key: 'blockedBy', header: 'Blocked By', render: (r) => r.blockedBy || '—' },
    {
      key: 'action',
      header: 'Action',
      render: (r) => (
        <Button size='small' color='success' variant='outlined' onClick={() => unblock(r)}>
          Unblock
        </Button>
      ),
    },
  ]

  const exportCsv = () => {
    const head = ['Pincode', 'CourierAccount', 'MerchantScope', 'Scope', 'Reason', 'Notes', 'BlockedOn', 'BlockedBy']
    const lines = rows.map((r) =>
      [
        r.pincode,
        r.courierCount > 1 ? `${r.courierCount} couriers` : r.courierAccountCode || '',
        r.merchantScope || '',
        r.scope || '',
        r.reasonLabel || r.reason || '',
        (r.notes || '').replace(/,/g, ';'),
        r.blockedOn || '',
        r.blockedBy || '',
      ].join(',')
    )
    downloadCSV([head.join(','), ...lines].join('\n'), 'currently_blocked_pincodes')
  }

  return (
    <IntelLayout title='Block Pincodes — Currently Blocked'>
      <Box
        sx={{
          background: '#fff',
          border: `1px solid ${intelColors.border}`,
          borderRadius: '8px',
          p: '14px 18px',
          mb: 2,
          display: 'flex',
          gap: 2,
          alignItems: 'flex-end',
          flexWrap: 'wrap',
        }}
      >
        <Autocomplete
          options={courierOptions}
          getOptionLabel={(o) => o.label || ''}
          isOptionEqualToValue={(a, b) => a.value === b.value}
          value={courierFilter}
          onChange={(_, v) => setCourierFilter(v)}
          size='small'
          sx={{ minWidth: 240 }}
          renderInput={(params) => <TextField {...params} label='Courier Account' placeholder='Search account code…' />}
        />
        <TextField select label='Block Scope' value={scope} onChange={(e) => setScope(e.target.value)} size='small' sx={{ minWidth: 160 }}>
          {SCOPE_FILTERS.map((o) => (
            <MenuItem key={o.value || 'all'} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {err && (
        <Box sx={{ background: '#ffebee', border: '1px solid #ef9a9a', color: '#c62828', borderRadius: '6px', p: '10px 14px', mb: '14px', fontSize: 12 }}>
          {err}
        </Box>
      )}

      <IntelTable
        title='Currently Blocked Pincodes'
        action={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Badge color='red'>{`${data?.totalRecords != null ? data.totalRecords : rows.length} active`}</Badge>
            <Button size='small' onClick={exportCsv}>
              ⬇ CSV
            </Button>
          </Box>
        }
        loading={loading}
        columns={columns}
        rows={rows}
        rowKey={(r, i) => r.id || i}
        pageSize={50}
        emptyMsg='No active blocks (new-UI blocks show here; legacy bulk-uploaded blocks are not listed)'
      />
    </IntelLayout>
  )
}
